import {
  ChevronLeftIcon,
  ChevronRightIcon,
  SearchIcon,
  MapIcon,
  PlusIcon,
  PencilIcon,
  TagIcon,
} from '@heroicons/react/outline';

import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import swal from 'sweetalert';

import {
  useGetClientRoutesOrSearchQuery,
  useGetClientRoutesPageChangeMutation,
  useGetClientRoutesSearchMutation,
} from '../../Features/clientsRoutesSlice';

import { selectCurrentUser } from '../../Features/auth/authSlice';
import { useSelector } from 'react-redux';

import LoadingBar from 'react-top-loading-bar';
import { useIsOnline } from 'react-use-is-online';

import ReactPaginate from 'react-paginate';
import '../../Pagination.css';

import { useTheme } from '../../ThemeContext';


function RoutesManagementView({}) {

  const { darkMode } = useTheme();

  const navigate = useNavigate();

  const [pager, setPager] = useState('');

  const user = useSelector(selectCurrentUser);

  const [progress, setProgress] = useState(0);

  const { isOnline } = useIsOnline();

  const [numRowsPerPage, setNumRowsPerPage] = useState(10);

  const [currentItems, setCurrentItems] = useState([]);

  const [pageCount, setPageCount] = useState(0);

  const [input, setInput] = useState({
    search: '',
  });


  /*
  |--------------------------------------------------------------------------
  | GET ROUTES
  |--------------------------------------------------------------------------
  */

  const {
    data: routes,
    isLoading,
    isSuccess,
    isError,
  } = useGetClientRoutesOrSearchQuery({
    paginate: numRowsPerPage,
    search: 0,
    page: pager,
    id: user.id,
  });


  /*
  |--------------------------------------------------------------------------
  | SEARCH INPUT
  |--------------------------------------------------------------------------
  */

  const handleChange = (e) => {

    setInput({
      ...input,
      [e.target.name]: e.target.value,
    });

  };


  /*
  |--------------------------------------------------------------------------
  | INITIAL DATA
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    setProgress(40);

    if (isLoading) {
      setProgress(80);
    }

    if (isSuccess && routes?.data) {

      setCurrentItems(
        routes?.data?.data || []
      );

      setPageCount(
        Math.ceil(
          routes.data.total /
          numRowsPerPage
        )
      );

    }

    setProgress(100);

  }, [
    routes,
    isLoading,
    isSuccess,
    numRowsPerPage,
  ]);


  /*
  |--------------------------------------------------------------------------
  | PAGE CHANGE
  |--------------------------------------------------------------------------
  */

  const [pageChange] =
    useGetClientRoutesPageChangeMutation();


  const onChangePageRows = async (rows) => {

    const rowsPerPage =
      window.parseInt(rows);

    const searchValue =
      input.search
        ? input.search
        : 0;


    if (!isOnline) {

      return swal(
        'No Internet connection',
        'Please check Internet connection and try again'
      );

    }


    try {

      setProgress(80);


      const resp =
        await pageChange({
          id: user.id,
          paginate: rows,
          search: searchValue,
          page: 1,
        }).unwrap();


      if (resp.success) {

        setCurrentItems(
          resp?.data?.data || []
        );


        setPageCount(
          Math.ceil(
            resp.data.total /
            rowsPerPage
          )
        );


        setNumRowsPerPage(
          rowsPerPage
        );

      }


      setProgress(100);

    } catch (err) {

      setProgress(100);

      swal(
        'Data not found.'
      );

    }

  };


  /*
  |--------------------------------------------------------------------------
  | PAGINATION
  |--------------------------------------------------------------------------
  */

  const handlePageChange = async (
    currentPage
  ) => {

    const pageNum =
      window.parseInt(
        currentPage
      ) + 1;


    const searchValue =
      input.search
        ? input.search
        : 0;


    try {

      setProgress(80);


      const resp =
        await pageChange({
          id: user.id,
          paginate: numRowsPerPage,
          search: searchValue,
          page: pageNum,
        }).unwrap();


      if (resp.success) {

        setCurrentItems(
          resp?.data?.data || []
        );


        setPageCount(
          Math.ceil(
            resp.data.total /
            numRowsPerPage
          )
        );

      }


      setProgress(100);

    } catch (err) {

      setProgress(100);

      swal(
        'Error',
        err?.message ||
        'Unable to load routes',
        'error'
      );

    }

  };


  /*
  |--------------------------------------------------------------------------
  | SEARCH
  |--------------------------------------------------------------------------
  */

  const [searchData] =
    useGetClientRoutesSearchMutation();


  const submitSearch = async (e) => {

    e.preventDefault();


    try {

      setProgress(80);


      const resp =
        await searchData({
          id: user.id,
          paginate: numRowsPerPage,
          search: input.search,
          page: 1,
        }).unwrap();


      if (resp.success) {

        const data =
          resp?.data?.data ||
          resp?.data ||
          [];


        setCurrentItems(data);


        if (resp?.data?.total !== undefined) {

          setPageCount(
            Math.ceil(
              resp.data.total /
              numRowsPerPage
            )
          );

        }

      }


      setProgress(100);

    } catch (err) {

      setInput({
        search: '',
      });

      setCurrentItems([]);

      setProgress(100);

      swal(
        'No results found...'
      );

    }

  };


  /*
  |--------------------------------------------------------------------------
  | EDIT ROUTE
  |--------------------------------------------------------------------------
  */

  const EditRoute = (
    e,
    route_id,
    user_route_id
  ) => {

    navigate(
      `/client/edit-user-route/${route_id}/${user_route_id}`
    );

  };


  /*
  |--------------------------------------------------------------------------
  | TEXT TRUNCATION
  |--------------------------------------------------------------------------
  */

  const trancateText = (str) => {

    if (
      str === null ||
      str === undefined
    ) {
      return '--';
    }


    const value =
      String(str);


    return value.length > 30
      ? value.substring(0, 30) + '...'
      : value;

  };


  /*
  |--------------------------------------------------------------------------
  | MONEY FORMAT
  |--------------------------------------------------------------------------
  */

  const formatMoney = (value) => {

    return Number(
      value ?? 0
    ).toLocaleString(
      'en-ZM',
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    );

  };


  /*
  |--------------------------------------------------------------------------
  | MARGIN
  |--------------------------------------------------------------------------
  */

  const calculateMargin = (
    price,
    cost
  ) => {

    if (
      price === null ||
      price === undefined ||
      cost === null ||
      cost === undefined ||
      Number(price) === 0
    ) {
      return null;
    }


    return (
      (
        (
          Number(price) -
          Number(cost)
        ) /
        Number(price)
      ) * 100
    ).toFixed(2);

  };


  /*
  |--------------------------------------------------------------------------
  | DISCOUNT DISPLAY
  |--------------------------------------------------------------------------
  */

  const getDiscount = (data) => {

    if (
      data.discount_name === null ||
      data.discount_name === undefined
    ) {
      return '--';
    }


    if (data.discount_type === 1) {

      return `${data.discount_name} (${data.discount_value}%)`;

    }


    return `${data.discount_name} (K${formatMoney(
      data.discount_value
    )})`;

  };


  /*
  |--------------------------------------------------------------------------
  | RENDER
  |--------------------------------------------------------------------------
  */

  return (

    <div className="
      w-full
      min-h-screen
      bg-gray-100
      dark:bg-gray-950
      pb-12
    ">

      <LoadingBar
        color="#80b643"
        height="4px"
        progress={progress}
        onLoaderFinished={() =>
          setProgress(0)
        }
      />


      <div className="
        w-full
        px-4
        sm:px-6
        lg:px-8
        py-6
      ">


        {/* =========================================================
            PAGE HEADER
        ========================================================= */}

        <div className="
          flex
          flex-col
          sm:flex-row
          sm:items-center
          sm:justify-between
          gap-4
          mb-6
        ">

          <div className="
            flex
            items-center
            gap-3
          ">

            <div className="
              w-11
              h-11
              rounded-xl
              bg-[#80b643]/10
              dark:bg-[#80b643]/20
              flex
              items-center
              justify-center
              flex-shrink-0
            ">

              <MapIcon
                className="
                  w-6
                  h-6
                  text-[#80b643]
                "
              />

            </div>


            <div>

              <h1 className="
                text-lg
                sm:text-xl
                font-semibold
                text-gray-800
                dark:text-white
              ">
                Manage Routes
              </h1>


              <p className="
                text-sm
                text-gray-500
                dark:text-gray-400
              ">
                Manage your transport routes, pricing and discounts
              </p>

            </div>

          </div>


          {/* ADD ROUTE */}

          <Link
            to="/client/user-route-register"
            className="
              inline-flex
              items-center
              justify-center
              gap-2
              w-full
              sm:w-auto
              px-4
              py-2.5
              rounded-xl
              bg-[#80b643]
              hover:bg-[#719f3a]
              text-white
              text-sm
              font-medium
              shadow-sm
              hover:shadow-md
              transition-all
            "
          >

            <PlusIcon
              className="
                w-5
                h-5
              "
            />

            Add Route

          </Link>

        </div>


        {/* =========================================================
            SEARCH / FILTER CARD
        ========================================================= */}

        <div className="
          bg-white
          dark:bg-gray-900
          border
          border-gray-200
          dark:border-gray-800
          rounded-xl
          shadow-sm
          p-4
          mb-5
        ">

          <div className="
            flex
            flex-col
            sm:flex-row
            sm:items-end
            sm:justify-between
            gap-4
          ">


            {/* SEARCH */}

            <form
              onSubmit={submitSearch}
              className="
                w-full
                sm:max-w-md
              "
            >

              <label className="
                block
                text-xs
                font-semibold
                uppercase
                tracking-wider
                text-gray-500
                dark:text-gray-400
                mb-2
              ">
                Search Routes
              </label>


              <div className="
                relative
                flex
                items-center
              ">

                <SearchIcon
                  className="
                    w-4
                    h-4
                    absolute
                    left-3
                    text-[#80b643]
                    pointer-events-none
                  "
                />


                <input
                  type="text"
                  name="search"
                  value={input.search}
                  onChange={handleChange}
                  placeholder="Search route, station or category..."
                  autoComplete="off"
                  aria-label="Search routes"
                  className="
                    w-full
                    bg-gray-50
                    dark:bg-gray-800
                    border
                    border-gray-200
                    dark:border-gray-700
                    rounded-lg
                    pl-9
                    pr-4
                    py-2.5
                    text-sm
                    text-gray-700
                    dark:text-gray-200
                    placeholder-gray-400
                    dark:placeholder-gray-500
                    hover:border-[#80b643]
                    focus:outline-none
                    focus:border-[#80b643]
                    focus:ring-1
                    focus:ring-[#80b643]/30
                    transition-colors
                  "
                />

              </div>

            </form>


            {/* SEARCH BUTTON */}

            <button
              type="button"
              onClick={submitSearch}
              className="
                inline-flex
                items-center
                justify-center
                gap-2
                w-full
                sm:w-auto
                px-4
                py-2.5
                rounded-lg
                bg-[#80b643]
                hover:bg-[#719f3a]
                text-white
                text-sm
                font-medium
                shadow-sm
                transition-all
              "
            >

              <SearchIcon
                className="
                  w-4
                  h-4
                "
              />

              Search

            </button>

          </div>

        </div>


        {/* =========================================================
            MAIN TABLE CARD
        ========================================================= */}

        <div className="
          bg-white
          dark:bg-gray-900
          border
          border-gray-200
          dark:border-gray-800
          rounded-xl
          shadow-sm
          overflow-hidden
        ">


          {/* TABLE HEADER */}

          <div className="
            flex
            flex-col
            sm:flex-row
            sm:items-center
            sm:justify-between
            gap-2
            px-4
            sm:px-5
            py-4
            border-b
            border-gray-200
            dark:border-gray-800
          ">

            <div>

              <h2 className="
                text-sm
                sm:text-base
                font-semibold
                text-gray-800
                dark:text-white
              ">
                Routes
              </h2>


              <p className="
                text-xs
                sm:text-sm
                text-gray-500
                dark:text-gray-400
                mt-0.5
              ">
                View and manage your configured transport routes
              </p>

            </div>


            {currentItems.length > 0 && (

              <div className="
                text-xs
                text-gray-400
                dark:text-gray-500
              ">
                {currentItems.length} route
                {currentItems.length === 1
                  ? ''
                  : 's'} displayed
              </div>

            )}

          </div>


          {/* =======================================================
              TABLE
          ======================================================= */}

          {currentItems?.length > 0 ? (

            <div className="overflow-x-auto">

              <table className="
                w-full
                min-w-[1050px]
              ">

                <thead className="
                  bg-gray-50
                  dark:bg-gray-800/70
                  border-b
                  border-gray-200
                  dark:border-gray-800
                ">

                  <tr>

                    <th
                      scope="col"
                      className="
                        px-5
                        py-3.5
                        text-left
                        text-[11px]
                        font-semibold
                        uppercase
                        tracking-wider
                        text-gray-500
                        dark:text-gray-400
                      "
                    >
                      Route
                    </th>


                    <th
                      scope="col"
                      className="
                        px-5
                        py-3.5
                        text-left
                        text-[11px]
                        font-semibold
                        uppercase
                        tracking-wider
                        text-gray-500
                        dark:text-gray-400
                      "
                    >
                      Category
                    </th>


                    <th
                      scope="col"
                      className="
                        px-5
                        py-3.5
                        text-right
                        text-[11px]
                        font-semibold
                        uppercase
                        tracking-wider
                        text-gray-500
                        dark:text-gray-400
                      "
                    >
                      Price
                    </th>


                    <th
                      scope="col"
                      className="
                        px-5
                        py-3.5
                        text-right
                        text-[11px]
                        font-semibold
                        uppercase
                        tracking-wider
                        text-gray-500
                        dark:text-gray-400
                      "
                    >
                      Cost
                    </th>


                    <th
                      scope="col"
                      className="
                        px-5
                        py-3.5
                        text-left
                        text-[11px]
                        font-semibold
                        uppercase
                        tracking-wider
                        text-gray-500
                        dark:text-gray-400
                      "
                    >
                      Discount
                    </th>


                    <th
                      scope="col"
                      className="
                        px-5
                        py-3.5
                        text-center
                        text-[11px]
                        font-semibold
                        uppercase
                        tracking-wider
                        text-gray-500
                        dark:text-gray-400
                      "
                    >
                      Period
                    </th>


                    <th
                      scope="col"
                      className="
                        px-5
                        py-3.5
                        text-right
                        text-[11px]
                        font-semibold
                        uppercase
                        tracking-wider
                        text-gray-500
                        dark:text-gray-400
                      "
                    >
                      Margin
                    </th>


                    <th
                      scope="col"
                      className="
                        px-5
                        py-3.5
                        text-right
                        text-[11px]
                        font-semibold
                        uppercase
                        tracking-wider
                        text-gray-500
                        dark:text-gray-400
                      "
                    >
                      Action
                    </th>

                  </tr>

                </thead>


                <tbody className="
                  divide-y
                  divide-gray-100
                  dark:divide-gray-800
                ">

                  {currentItems.map(
                    (data) => {

                      const margin =
                        calculateMargin(
                          data.price,
                          data.cost
                        );


                      return (

                        <tr
                          onClick={(e) =>
                            EditRoute(
                              e,
                              data.route_id,
                              data.user_route_id
                            )
                          }
                          key={
                            data.user_route_id
                          }
                          className="
                            group
                            cursor-pointer
                            hover:bg-gray-50
                            dark:hover:bg-gray-800/50
                            transition-colors
                          "
                        >


                          {/* ROUTE */}

                          <td className="
                            px-5
                            py-4
                          ">

                            <div className="
                              flex
                              items-center
                              gap-3
                            ">

                              <div className="
                                w-9
                                h-9
                                rounded-lg
                                bg-[#80b643]/10
                                dark:bg-[#80b643]/20
                                flex
                                items-center
                                justify-center
                                flex-shrink-0
                              ">

                                <MapIcon
                                  className="
                                    w-4
                                    h-4
                                    text-[#80b643]
                                  "
                                />

                              </div>


                              <div className="
                                min-w-0
                              ">

                                <div className="
                                  text-sm
                                  font-medium
                                  text-gray-800
                                  dark:text-white
                                  max-w-[280px]
                                ">
                                  {trancateText(
                                    `${data.start_off_station_name} - ${data.destination_station_name}`
                                  )}
                                </div>


                                <div className="
                                  text-xs
                                  text-gray-400
                                  dark:text-gray-500
                                  mt-0.5
                                ">
                                  Route
                                </div>

                              </div>

                            </div>

                          </td>


                          {/* CATEGORY */}

                          <td className="
                            px-5
                            py-4
                          ">

                            {data.category !== null ? (

                              <span className="
                                inline-flex
                                items-center
                                px-2.5
                                py-1
                                rounded-lg
                                bg-gray-100
                                dark:bg-gray-800
                                text-gray-600
                                dark:text-gray-300
                                text-xs
                                font-medium
                              ">

                                {trancateText(
                                  data.category
                                )}

                              </span>

                            ) : (

                              <span className="
                                text-sm
                                text-gray-400
                                dark:text-gray-500
                              ">
                                No category
                              </span>

                            )}

                          </td>


                          {/* PRICE */}

                          <td className="
                            px-5
                            py-4
                            text-right
                          ">

                            {data.price !== null ? (

                              <span className="
                                text-sm
                                font-semibold
                                text-gray-800
                                dark:text-white
                              ">
                                K {formatMoney(
                                  data.price
                                )}
                              </span>

                            ) : (

                              <span className="
                                text-sm
                                text-gray-400
                              ">
                                --
                              </span>

                            )}

                          </td>


                          {/* COST */}

                          <td className="
                            px-5
                            py-4
                            text-right
                          ">

                            {data.cost !== null ? (

                              <span className="
                                text-sm
                                text-gray-600
                                dark:text-gray-300
                              ">
                                K {formatMoney(
                                  data.cost
                                )}
                              </span>

                            ) : (

                              <span className="
                                text-sm
                                text-gray-400
                              ">
                                --
                              </span>

                            )}

                          </td>


                          {/* DISCOUNT */}

                          <td className="
                            px-5
                            py-4
                          ">

                            {data.discount_name !== null ? (

                              <div className="
                                flex
                                items-center
                                gap-2
                              ">

                                <div className="
                                  w-7
                                  h-7
                                  rounded-lg
                                  bg-[#80b643]/10
                                  dark:bg-[#80b643]/20
                                  flex
                                  items-center
                                  justify-center
                                  flex-shrink-0
                                ">

                                  <TagIcon
                                    className="
                                      w-3.5
                                      h-3.5
                                      text-[#80b643]
                                    "
                                  />

                                </div>


                                <span className="
                                  text-xs
                                  text-gray-600
                                  dark:text-gray-300
                                ">
                                  {getDiscount(data)}
                                </span>

                              </div>

                            ) : (

                              <span className="
                                text-sm
                                text-gray-400
                                dark:text-gray-500
                              ">
                                --
                              </span>

                            )}

                          </td>


                          {/* PERIOD */}

                          <td className="
                            px-5
                            py-4
                            text-center
                          ">

                            {data.period !== null ? (

                              <span className="
                                inline-flex
                                items-center
                                px-2.5
                                py-1
                                rounded-lg
                                bg-gray-100
                                dark:bg-gray-800
                                text-gray-600
                                dark:text-gray-300
                                text-xs
                                font-medium
                              ">
                                {data.period} Hrs
                              </span>

                            ) : (

                              <span className="
                                text-sm
                                text-gray-400
                              ">
                                --
                              </span>

                            )}

                          </td>


                          {/* MARGIN */}

                          <td className="
                            px-5
                            py-4
                            text-right
                          ">

                            {margin !== null ? (

                              <span className="
                                inline-flex
                                items-center
                                px-2.5
                                py-1
                                rounded-lg
                                bg-[#80b643]/10
                                dark:bg-[#80b643]/20
                                text-[#80b643]
                                text-xs
                                font-semibold
                              ">
                                {margin}%
                              </span>

                            ) : (

                              <span className="
                                text-sm
                                text-gray-400
                              ">
                                --
                              </span>

                            )}

                          </td>


                          {/* ACTION */}

                          <td className="
                            px-5
                            py-4
                            text-right
                          ">

                            <button
                              type="button"
                              onClick={(e) => {

                                e.stopPropagation();

                                EditRoute(
                                  e,
                                  data.route_id,
                                  data.user_route_id
                                );

                              }}
                              title="Edit route"
                              className="
                                inline-flex
                                items-center
                                justify-center
                                w-8
                                h-8
                                rounded-lg
                                text-gray-400
                                hover:text-[#80b643]
                                hover:bg-[#80b643]/10
                                dark:hover:bg-[#80b643]/20
                                transition-colors
                              "
                            >

                              <PencilIcon
                                className="
                                  w-4
                                  h-4
                                "
                              />

                            </button>

                          </td>

                        </tr>

                      );

                    }
                  )}

                </tbody>

              </table>

            </div>

          ) : (

            /* =====================================================
               EMPTY STATE
            ===================================================== */

            <div className="
              flex
              flex-col
              items-center
              justify-center
              px-6
              py-16
            ">

              <div className="
                w-14
                h-14
                rounded-xl
                bg-gray-100
                dark:bg-gray-800
                flex
                items-center
                justify-center
                mb-4
              ">

                <MapIcon
                  className="
                    w-7
                    h-7
                    text-gray-400
                    dark:text-gray-500
                  "
                />

              </div>


              <h3 className="
                text-sm
                font-semibold
                text-gray-700
                dark:text-gray-200
              ">
                No routes found
              </h3>


              <p className="
                text-xs
                sm:text-sm
                text-gray-500
                dark:text-gray-400
                text-center
                max-w-sm
                mt-1.5
              ">
                No routes match your current search. Add a new route or try a different search.
              </p>


              <Link
                to="/client/user-route-register"
                className="
                  inline-flex
                  items-center
                  justify-center
                  gap-2
                  mt-5
                  px-4
                  py-2.5
                  rounded-xl
                  bg-[#80b643]
                  hover:bg-[#719f3a]
                  text-white
                  text-sm
                  font-medium
                  shadow-sm
                  hover:shadow-md
                  transition-all
                "
              >

                <PlusIcon
                  className="
                    w-5
                    h-5
                  "
                />

                Add Route

              </Link>

            </div>

          )}


          {/* =======================================================
              PAGINATION
          ======================================================= */}

          {currentItems?.length > 0 && (

            <div className="
              flex
              flex-col
              sm:flex-row
              sm:items-center
              sm:justify-between
              gap-4
              px-4
              sm:px-5
              py-4
              border-t
              border-gray-200
              dark:border-gray-800
            ">


              {/* PAGINATION */}

              <div className="
                overflow-x-auto
                max-w-full
              ">

                <ReactPaginate
                  breakLabel="..."
                  nextLabel={
                    <ChevronRightIcon
                      className="
                        h-7
                        w-7
                        text-gray-400
                        dark:text-gray-300
                      "
                    />
                  }
                  onPageChange={(e) =>
                    handlePageChange(
                      e.selected
                    )
                  }
                  pageRangeDisplayed={3}
                  pageCount={pageCount}
                  previousLabel={
                    <ChevronLeftIcon
                      className="
                        h-7
                        w-7
                        text-gray-400
                        dark:text-gray-300
                      "
                    />
                  }
                  renderOnZeroPageCount={null}
                  containerClassName="pagination"
                  pageLinkClassName="page-num"
                  previousLinkClassName="controlBtn"
                  nextLinkClassName="controlBtn"
                  activeLinkClassName={`
                    ${
                      darkMode
                        ? ''
                        : 'active'
                    }
                    dark:text-white
                    bg-white
                    dark:bg-gray-700
                    hover:dark:text-white
                    hover:dark:bg-gray-600
                  `}
                />

              </div>


              {/* ROWS PER PAGE */}

              <div className="
                flex
                items-center
                justify-between
                sm:justify-end
                gap-2
                flex-shrink-0
              ">

                <span className="
                  text-xs
                  text-gray-500
                  dark:text-gray-400
                ">
                  Rows per page
                </span>


                <select
                  value={numRowsPerPage}
                  onChange={(e) =>
                    onChangePageRows(
                      e.target.value
                    )
                  }
                  className="
                    w-20
                    px-2
                    py-1.5
                    rounded-lg
                    bg-white
                    dark:bg-gray-800
                    border
                    border-gray-200
                    dark:border-gray-700
                    text-xs
                    text-gray-600
                    dark:text-gray-300
                    hover:border-[#80b643]
                    focus:outline-none
                    focus:border-[#80b643]
                    focus:ring-1
                    focus:ring-[#80b643]/30
                    cursor-pointer
                  "
                >

                  <option value="10">
                    10
                  </option>

                  <option value="25">
                    25
                  </option>

                  <option value="50">
                    50
                  </option>

                  <option value="100">
                    100
                  </option>

                </select>

              </div>

            </div>

          )}

        </div>


        {/* =========================================================
            FOOTER
        ========================================================= */}

        {currentItems?.length > 0 && (

          <div className="
            mt-3
            px-1
            text-xs
            text-gray-500
            dark:text-gray-400
          ">

            Showing{' '}

            <span className="
              font-medium
              text-gray-700
              dark:text-gray-200
            ">
              {currentItems.length}
            </span>{' '}

            route
            {currentItems.length === 1
              ? ''
              : 's'}

          </div>

        )}

      </div>

    </div>

  );
}


export default RoutesManagementView;